<template>
    <div class='recurring-commissionplan-settings'>
      <dean-two-column-layout :title="headerTitle">
        <dean-section-title-item
            :icon="saveIcon"
            slot="title-items"
            :content="true"
            :large="true"
            @click.native="save"
        >
          Opslaan
        </dean-section-title-item>
        <div class="column" slot="left">
          <dean-form-row key="name" id="name" label="naam" v-model="data.name" type="text" :required-field="true"
                        :warning="$v.data.name.$error" @focus="$v.data.name.$touch()"/>
          <dean-multi-select-form-row :multiple="true"
                                      placeholder="Partner types"
                                      id="partnerAgreementTypes"
                                      label="Partner types"
                                      :value="selectedTypes" :options='availablePartnerAgreementTypes'
                                      @select='addType'
                                      @remove="removeType"></dean-multi-select-form-row>
        </div>
      </dean-two-column-layout>
    </div>
</template>

<script lang="ts">
  import {Vue, Component} from 'vue-property-decorator';
  import axios from 'axios';
  import {
    deanSectionTitleItem,
    saveIcon,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import deanMultiSelectFormRow from '@/components/deanComponents/deanMultiSelectFormRow.vue';
  import {mixins} from 'vue-class-component';
  import periodsSideMenuMixin from '@/mixins/periodsSideMenuMixin';
  import {Validations} from 'vuelidate-property-decorators';
  import {required} from 'vuelidate/lib/validators';
  import {createNotification} from '@/mixins/validations';

  @Component({
    components: {
      deanSectionTitleItem,
      DeanFormRow,
      DeanTwoColumnLayout,
      deanMultiSelectFormRow,
    },
  })
  export default class Settings extends mixins(periodsSideMenuMixin) {
    // DATA
    public data: any = {
      name: '',
      partnerAgreementTypes: [],
    };
    public partnerAgreementTypes: any[] = [];
    public availablePartnerAgreementTypes: any[] = [];
    public saveIcon: any = saveIcon;
    public commissionName: string = '';

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          name: {
            required,
            },
          },
        };
    }

    get headerTitle(): string {
      return (this.$route.params.planId !== undefined) ? 'Commissieplan wijzigen' : 'Commissieplan toevoegen';
    }

    get selectedTypes() {
      this.updateAvailablePartnerAgreementTypeOptions();
      return this.data.partnerAgreementTypes;
    }

    public addType(field: any) {
      this.data.partnerAgreementTypes.push(field);
      this.updateAvailablePartnerAgreementTypeOptions();
    }

    public removeType(field: any) {
      this.data.partnerAgreementTypes = this.data.partnerAgreementTypes.filter((partnerAgreementType: any) => {
         if (partnerAgreementType.value !== field.value) {
           return partnerAgreementType;
         }
      });
      this.updateAvailablePartnerAgreementTypeOptions();
    }

    public updateAvailablePartnerAgreementTypeOptions() {
      if (this.data.partnerAgreementTypes.length <= 0) {
        this.availablePartnerAgreementTypes = this.partnerAgreementTypes;
        return;
      }

      const selectedTypes: any[] = [];

      this.data.partnerAgreementTypes.forEach((type: any) => {
        selectedTypes[type.value] = type.label;
      });

      this.availablePartnerAgreementTypes = this.partnerAgreementTypes.filter((partnerAgreementType: any) => {
        if (selectedTypes[partnerAgreementType.value] === undefined) {
          return partnerAgreementType;
        }
      });
    }

    get sideMenu(): object {
      return {
        content: {
          title: {
            text: 'Commissieplan ' + (this.commissionName ? this.commissionName : ''),
          },
          type: 'themed',
          sections: {
            general: {
              items: {
                settings: {
                  title: 'Instellingen',
                  name: 'standingData.recurringCommissionPlan.edit',
                  state: 'active',
                },
                back: {
                  title: 'Terug naar Commissieplannen',
                  name: 'standingData.recurringCommissionPlans',
                },
              },
            },
            // periods: {
            //   title: 'periode',
            //   items: this.periods,
            // },
          },
        },
      };
    }

    public save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);
        axios.post('/-/trade/api/settings.recurringCommissionPlan.save', {
          id: this.$route.params.planId,
          data: this.data,
        }).then(
          (response: any) => {
            this.$toast(this.$store.state.main.notification.messages.dataSaved);
          },
          (error: any) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          },
        ).finally(() => {
          this.$store.dispatch('setLoadOverlay', false);
        });
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public created() {
      this.$store.dispatch('setLoadOverlay', true);

      this.initPage();
    }

    public initPage() {
      let promise;

      if (this.$route.params.planId) {
        promise = this.$store.dispatch('requestRecurringCommissionPlan', {
          id: this.$route.params.planId,
          providerId: this.$route.params.planId ? undefined : 'c2c286fa-f280-4763-b306-81cef170d73b',
        })
        .then(
          (response: any) => {
            this.data.name = response.data.name;
            this.data.partnerAgreementTypes = response.data.partnerAgreementTypes;

            this.data.partnerAgreementTypes = response.data.partnerAgreementTypes.map((partnerAgreementType: any) => {
              return {
                value: partnerAgreementType.id,
                label: partnerAgreementType.name,
              };
            });

            this.commissionName = response.data.name;

            return this.$store.dispatch('requestPartnerAgreementTypes');
          },
          (error: any) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
          },
        );
      } else {
        promise = this.$store.dispatch('requestPartnerAgreementTypes');
      }

      promise
      .then(
        (partnerAgreementTypeResponse: any) => {
          this.partnerAgreementTypes = partnerAgreementTypeResponse.data.data;

          this.partnerAgreementTypes = partnerAgreementTypeResponse.data.data.map((partnerAgreementType: any) => {
            return {
              value: partnerAgreementType.id,
              label: partnerAgreementType.name,
            };
          });

          this.$store.dispatch('updateSideMenu', this.sideMenu);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }
  }
</script>

<style lang="scss" scoped>

</style>
